/**
 * @file CustomerFormulationsPanel.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-05
 * @modified 2026-10-05
 * @description Contact-modal panel: this customer's blend formulations, by
 *              product type (their blend lines — Milkers, Beef…), newest
 *              generation first — and "New formulation", which makes the next
 *              one (item, formulation and placement) without leaving the
 *              customer's record (NewCustomerFormulationModal).
 *
 *              Read from GET /api/formulations/customer-blends?account_id=, the
 *              same list the Formulations screen groups by, so the two cannot
 *              disagree. A row opens the formulation in the shared modal, where
 *              viewing and editing live — this panel edits nothing itself.
 *
 *              The button is always in its place: disabled with the reason for
 *              a view-only account or one without both permissions it needs.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { API_URL } from '../config';
import FormulationModal from './FormulationModal';
import ItemStatusBadge from './ItemStatusBadge';
import NewCustomerFormulationModal, { useNewCustomerFormulationAccess } from './NewCustomerFormulationModal';
import { useSlideOutButton } from './SlideOutButton';
import { formatDateShort } from '../utils/dateFormatters';
import './CustomerFormulationsPanel.css';

/**
 * @param {object} contact  the customer: { id, company_name, account_no }
 */
function CustomerFormulationsPanel({ contact }) {
  const accountId = contact?.id;
  const access = useNewCustomerFormulationAccess();
  const [lines, setLines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [creating, setCreating] = useState(false);
  const [notice, setNotice] = useState('');
  const [viewingId, setViewingId] = useState(null);
  const [components, setComponents] = useState(null);

  useSlideOutButton([access.allowed, loading]);

  const load = useCallback(async () => {
    if (!accountId) return;
    setLoading(true);
    try {
      const res = await fetch(
        `${API_URL}/api/formulations/customer-blends?account_id=${encodeURIComponent(accountId)}&includeArchived=true`,
        { credentials: 'include' }
      );
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || 'Failed to load formulations');
      setLines(data.blends || []);
      setError('');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [accountId]);

  useEffect(() => { load(); }, [load]);

  // The view modal needs the component registry; fetched the first time a row
  // is opened, not with the panel.
  const openFormulation = async (formulationId) => {
    if (!components) {
      try {
        const res = await fetch(`${API_URL}/api/formulations/components`, { credentials: 'include' });
        const data = await res.json();
        setComponents(data.components || []);
      } catch (err) {
        setError(err.message);
        return;
      }
    }
    setViewingId(formulationId);
  };

  const rows = lines.flatMap(line => (line.generations || []).map((g, idx) => ({
    ...g, line_name: line.name, line_archived: line.is_archived, isCurrent: idx === 0
  })));

  return (
    <div className="customer-formulations-panel">
      <div className="panel-toolbar">
        <div className="toolbar-stats">
          {rows.length} formulation{rows.length === 1 ? '' : 's'}
          {lines.length > 0 && <> · {lines.length} product type{lines.length === 1 ? '' : 's'}</>}
        </div>
        <div className="buttons slideout">
          <div>
            <button
              className="btn-slideout"
              data-write-action
              onClick={() => { setNotice(''); setCreating(true); }}
              disabled={!access.allowed || !accountId}
              title={access.reason || 'Create the item, its formulation and its place under this customer in one go'}
            >
              <div className="icon-wrapper"><span className="material-symbols-rounded">add</span></div>
              <span className="btn-label">New formulation</span>
            </button>
          </div>
        </div>
      </div>

      {notice && <div className="cfp-notice">{notice}</div>}

      {loading ? (
        <div className="cfp-info">Loading…</div>
      ) : error ? (
        <div className="cfp-info cfp-error">{error}</div>
      ) : rows.length === 0 ? (
        <div className="cfp-info">No blend formulations filed under this customer yet.</div>
      ) : (
        <div className="table-wrapper">
          <table className="data-table">
            <thead>
              <tr>
                <th>Product type</th>
                <th>Product</th>
                <th>First sold</th>
                <th className="status-col">Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr
                  key={r.formulation_id}
                  className={`cfp-row${r.is_archived || r.is_locally_archived || r.line_archived ? ' inactive-row' : ''}`}
                  onClick={() => openFormulation(r.formulation_id)}
                  title="View formulation"
                >
                  <td>{r.isCurrent ? r.line_name : <span className="cfp-muted">{r.line_name}</span>}</td>
                  <td>
                    <div>{r.item_name}</div>
                    <div className="cfp-code">{r.item_code}</div>
                  </td>
                  <td>{r.first_sold ? formatDateShort(r.first_sold) : <span className="cfp-muted">Not yet</span>}</td>
                  <td className="status-col"><ItemStatusBadge item={r} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {creating && (
        <NewCustomerFormulationModal
          account={contact}
          onCreated={(data) => setNotice(
            `Created ${data.item.item_code} · ${data.item.item_name} under ${data.placement.line_name}`
            + (data.xero?.pushed ? ' — in Xero.' : ' — not in Xero yet; the next inventory sync sends it.')
          )}
          // Closing refreshes the list behind it, created or not.
          onClose={() => { setCreating(false); load(); }}
        />
      )}

      {viewingId && components && (
        <FormulationModal
          mode="view"
          formulationId={viewingId}
          components={components}
          period={null}
          currentPeriod={null}
          onClose={() => { setViewingId(null); load(); }}
          onSaved={() => { setViewingId(null); load(); }}
          onIterationDeleted={load}
        />
      )}
    </div>
  );
}

export default CustomerFormulationsPanel;
